import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * DashboardLayout - Common layout for all dashboard pages
 * Includes header with navigation and user info, sidebar navigation, and content area
 *
 * @param {React.ReactNode} children - Main dashboard content
 * @param {string} title - Dashboard title
 * @param {Array} navItems - Array of navigation items [{label, path, icon}]
 * @param {string} userRole - User role for display
 * @param {string} userName - User name for display
 */
const DashboardLayout = ({
  children,
  title,
  navItems = [],
  userRole = '',
  userName = ''
}) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img
                src="/assets/images/brillabee logo.png"
                alt="Brillabee Logo"
                className="h-8 w-8 object-contain mr-2"
              />
              <h1 className="text-2xl font-bold text-indigo-600">Brillabee</h1>
              <span className="ml-3 text-sm text-gray-500">| {title}</span>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{userName}</p>
                <p className="text-xs text-gray-500 capitalize">{userRole.replace('-', ' ')}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-h-screen">
        {/* Sidebar Navigation */}
        {navItems.length > 0 && (
          <aside className="hidden md:block w-64 bg-white border-r border-gray-200 sticky top-16 self-start h-[calc(100vh-4rem)] overflow-y-auto">
            <nav className="p-4 space-y-1">
              {navItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    window.location.pathname === item.path
                      ? 'bg-indigo-50 text-indigo-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {item.icon && <span className="mr-3 text-lg">{item.icon}</span>}
                  {item.label}
                </Link>
              ))}
            </nav>
          </aside>
        )}

        {/* Main Content Area */}
        <main className="flex-1 w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
